import { useState } from 'react';
import {
  DailyOpenHoursSettings,
  DaySettings,
  TimeRange,
} from '@/shared/types/availability';
import { DayOfWeek } from '@/shared/types/generated';
import { localId as generateTimeRangeId } from '@/shared/components/schedule/utils';

const defaultTimeRange: TimeRange = {
  id: generateTimeRangeId(),
  startTime: '09:00',
  endTime: '17:00',
};

const defaultDaySettings: DaySettings = {
  enabled: false,
  timeRanges: [{ ...defaultTimeRange }],
};

const defaultEnabledDaySettings: DaySettings = {
  enabled: true,
  timeRanges: [{ ...defaultTimeRange }],
};

export const defaultSettings: DailyOpenHoursSettings = {
  [DayOfWeek.Monday]: { ...defaultEnabledDaySettings },
  [DayOfWeek.Tuesday]: { ...defaultEnabledDaySettings },
  [DayOfWeek.Wednesday]: { ...defaultEnabledDaySettings },
  [DayOfWeek.Thursday]: { ...defaultEnabledDaySettings },
  [DayOfWeek.Friday]: { ...defaultEnabledDaySettings },
  [DayOfWeek.Saturday]: { ...defaultDaySettings },
  [DayOfWeek.Sunday]: { ...defaultDaySettings },
};

export function useAvailabilitySettings(
  initialSettings?: DailyOpenHoursSettings,
) {
  const [settings, setSettings] = useState<DailyOpenHoursSettings>(
    initialSettings || defaultSettings,
  );

  const toggleDay = (day: DayOfWeek): void => {
    setSettings((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      },
    }));
  };

  const addTimeRange = (day: DayOfWeek): void => {
    setSettings((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeRanges: [
          ...prev[day].timeRanges,
          {
            id: generateTimeRangeId(),
            startTime: '09:00',
            endTime: '17:00',
          },
        ],
      },
    }));
  };

  const removeTimeRange = (day: DayOfWeek, rangeId: string): void => {
    setSettings((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeRanges: prev[day].timeRanges.filter((r) => r.id !== rangeId),
      },
    }));
  };

  const handleTimeChange = (
    day: DayOfWeek,
    rangeId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ): void => {
    setSettings((prev) => {
      const newSettings = { ...prev };
      const daySettings = newSettings[day];
      const timeRanges = [...daySettings.timeRanges];
      const rangeIndex = timeRanges.findIndex((r) => r.id === rangeId);

      if (rangeIndex !== -1) {
        timeRanges[rangeIndex] = {
          ...timeRanges[rangeIndex],
          [field]: value,
        };

        newSettings[day] = {
          ...daySettings,
          timeRanges,
        };
      }

      return newSettings;
    });
  };

  return {
    settings,
    toggleDay,
    addTimeRange,
    removeTimeRange,
    handleTimeChange,
  };
}
